// Deterministic per-round PRNG for replay/challenge URLs. Replaces every
// Math.random() call in spawn-affecting code paths so a (cycle, nonce) pair
// fully determines the enemy field. Without this, "race my ghost" links would
// produce different fields between players — broken artifact.
//
// mulberry32 is the right primitive here: fast, well-distributed, period 2^32
// (sufficient for ~30s rounds with <1k spawn decisions each). NOT cryptographic
// — visual seed only. crypto.getRandomValues is unsuitable because it is not
// seedable; we need the same seed → same sequence guarantee.

export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let s = seed >>> 0;
  return (): number => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Three entropy terms folded into one 32-bit seed:
//   visitorRaw — per-visitor (two visitors on the same day → different fields)
//   cycle      — per-day (today ≠ tomorrow)
//   nonce      — per-round (consecutive rounds vary; URL fixes this term)
// Multiplier constants are odd primes chosen for spread; the same shape as
// the visitor seed's FNV-1a in seed/per-visitor.ts:32.
export function seedForRound(visitorRaw: number, cycle: number, nonce: number): number {
  return (visitorRaw ^ (cycle * 0x9E3779B1) ^ (nonce * 0x85EBCA6B)) >>> 0;
}

export function randomNonce(): number {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0]!;
  }
  return Math.floor(Math.random() * 0xFFFFFFFF) >>> 0;
}
